import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, signal, Signal, SimpleChanges } from '@angular/core';
import { ButtonComponent } from '@lx/shared/ui/button';
import { Observable, take } from 'rxjs';
import { QuoteToolLiteStore } from '../../store';
import { PREMIUM_ACTIVITIES_FALLBACK_IMAGE, PremiumActivities } from '../../types';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { CardContainerComponent, ImageComponent, OptionCardComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelGalleryComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BasePremiumActivity } from '../../types/premium-activity.type';
import { OperationKey } from '../../constants';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-premium-activities',
  imports: [CardContainerComponent, OptionCardComponent, ButtonComponent, SidePanelGalleryComponent, NgForOf, AsyncPipe, ImageComponent, NgIf],
  templateUrl: './premium-activities.component.html',
  styleUrls: ['./premium-activities.component.scss'],
})
export class PremiumActivitiesComponent implements OnChanges, OnInit {
  @Input() premiumActivities: PremiumActivities | undefined;

  protected selected = false;
  protected images: string[] = [];
  protected premiumActivitiesDialogVisible = false;
  protected schoolLocation$: Observable<SchoolLocation> | undefined;
  protected readonly PREMIUM_ACTIVITIES_FALLBACK_IMAGE = PREMIUM_ACTIVITIES_FALLBACK_IMAGE;
  protected loading: Signal<boolean> = signal(false);

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.schoolLocation$ = this.quoteToolLiteStore.selectedSchoolLocation$;
    this.loading = this.loadingService.isOperationLoading(OperationKey.extrasSummary);
  }

  ngOnChanges({ premiumActivities }: SimpleChanges): void {
    if (premiumActivities?.currentValue) {
      this.images = this.getImages();
      this.quoteToolLiteStore.selectedPremiumActivities$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedPremiumActivities: BasePremiumActivity[]) => {
          this.selected = selectedPremiumActivities?.some(({ code }: BasePremiumActivity) => code === this.premiumActivities?.productCode);
        });
    }
  }

  onLearnMoreClick() {
    this.premiumActivitiesDialogVisible = true;
  }

  updateSelection(): void {
    this.selected = !this.selected;
    this.quoteToolLiteStore.updateState({ selectedPremiumActivities: this.prepareSelectedPremiumActivity() });
  }

  hidePremiumActivitiesDialog() {
    this.sidePanelService.hidePanel(() => (this.premiumActivitiesDialogVisible = false));
  }

  private getImages(): string[] {
    return this.premiumActivities?.extrasInfo.reduce((acc, premiumActivity) => {
      acc.push(premiumActivity.imageUrl);

      return acc;
    }, []);
  }

  private prepareSelectedPremiumActivity() {
    if (!this.selected) {
      return undefined;
    }

    return [
      <BasePremiumActivity>{
        code: this.premiumActivities?.productCode,
        units: 1, // Minimum default value, can be changed in the options
      },
    ];
  }
}
